import React, { FC, useState, useEffect } from 'react';
import { Controller, Control, FieldErrors, UseFormSetError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import InfoIcon from '@mui/icons-material/Info';
import { 
  Box, 
  Typography, 
  Grid, 
  IconButton, 
  Tooltip,
  TextField,
  Divider
} from '@mui/material';

import FileUploader from '@shared/components/FileUploader';
import { getContentType } from '@shared/utils';

import styles from './file-download-section.module.scss';

interface FileDownloadSectionProps {
  control: Control<any>;
  errors: FieldErrors<any>;
  isReadOnly: boolean;
  setError: UseFormSetError<any>;
  setValue: (name: string, value: any) => void;
  getValues: () => any;
  fieldName?: string;
}


const FileDownloadSection: FC<FileDownloadSectionProps> = ({
  control,
  errors,
  isReadOnly,
  setError,
  setValue,
  getValues,
  fieldName = 'urlButton'
}) => {
  const { t } = useTranslation();
  const [urlValue, setUrlValue] = useState<string>('');
  const [fileValue, setFileValue] = useState<File | string | null>(null);

  // Инициализация и реакция на изменения значений формы
  useEffect(() => {
    const initializeValue = () => {
      const allValues = getValues();
      
      // Проверяем fileDownloadUrl для FileUploader (файл) - приоритет выше
      const fileDownloadUrlValue = allValues?.fileDownloadUrl;
      // Проверяем urlButton для TextField (URL)
      const urlButtonValue = allValues?.[fieldName] || allValues?.urlButton;
      
      // Если есть fileDownloadUrl и он не пустой - показываем как файл в FileUploader
      if (fileDownloadUrlValue && typeof fileDownloadUrlValue === 'string' && fileDownloadUrlValue.trim() !== '') {
        setFileValue(fileDownloadUrlValue);
        // Очищаем URL, если есть файл
        setUrlValue('');
      } 
      // Если есть urlButton - проверяем его тип
      else if (urlButtonValue) {
        const contentType = getContentType(urlButtonValue);
        
        if (contentType === 'file') {
          // Если это File объект - показываем в FileUploader
          setFileValue(urlButtonValue as File);
          setUrlValue('');
        } else if (contentType === 'url' || typeof urlButtonValue === 'string') {
          // Если это URL (с протоколом) или строка - показываем в TextField
          // Любая строка в urlButton считается URL для TextField, даже без протокола
          const urlString = String(urlButtonValue || '');
          if (urlString.trim() !== '') {
            setUrlValue(urlString);
            setFileValue(null);
          } else {
            setUrlValue('');
            setFileValue(null);
          }
        } else {
          // Если тип неизвестен - очищаем
          setUrlValue('');
          setFileValue(null);
        }
      } else {
        // Если значения пустые - очищаем оба поля
        setUrlValue('');
        setFileValue(null);
      }
    };

    // Инициализируем сразу
    initializeValue();
    
    // И через небольшую задержку на случай асинхронной загрузки
    const timeoutId1 = setTimeout(initializeValue, 100);
    
    // Дополнительная проверка через большее время для отслеживания reset
    const timeoutId2 = setTimeout(initializeValue, 300);
    
    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
    };
  }, [getValues, fieldName]);

  // Синхронизируем изменения с формой
  useEffect(() => {
    if (urlValue && urlValue.trim() !== '') {
      // Сохраняем URL в urlButton
      setValue(fieldName || 'urlButton', urlValue);
      // Очищаем fileDownloadUrl при установке URL
      setValue('fileDownloadUrl', '');
    } else if (fileValue) {
      // Если это File объект - сохраняем в urlButton (будет обработан как файл)
      if (fileValue instanceof File) {
        setValue(fieldName || 'urlButton', fileValue);
        setValue('fileDownloadUrl', '');
      } 
      // Если это строка (URL файла) - сохраняем в fileDownloadUrl
      else if (typeof fileValue === 'string') {
        const fileUrlString = fileValue.trim();
        if (fileUrlString !== '') {
          setValue('fileDownloadUrl', fileUrlString);
          setValue(fieldName || 'urlButton', '');
        }
      }
    } else if (!urlValue && !fileValue) {
      // Очищаем оба поля - используем пустые строки вместо null
      setValue(fieldName || 'urlButton', '');
      setValue('fileDownloadUrl', '');
    }
  }, [urlValue, fileValue, setValue, fieldName]);

  return (
    <Box sx={{ mt: 4 }}>
      <Box className={styles.formSection}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h5" className={styles.sectionTitle}>
            {t('clientAll.urlButtonSectionTitle')}
          </Typography>
          <Tooltip 
            title={t('clientAll.urlButtonSectionTooltip')} 
            arrow 
            placement="top" 
            enterTouchDelay={0} 
            leaveTouchDelay={3000}
          >
            <IconButton size="small" sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* Поле для ввода ссылки */}
            <TextField
              fullWidth
              label={t('clientAll.enterUrl')}
              placeholder={t('clientAll.urlPlaceholder')}
              value={urlValue || ''} // Всегда строка, никогда null или undefined
              onChange={(e) => {
                const newUrl = e.target.value || '';
                setUrlValue(newUrl);
                // Очищаем файл при вводе URL
                setFileValue(null);
              }}
              disabled={isReadOnly}
              variant="outlined"
              error={false} // Убираем ошибку для поля ссылки
              helperText="" // Убираем helper text
              sx={{ mb: 3 }}
            />
            
            {/* Разделитель с текстом "ИЛИ" */}
            <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
              <Divider sx={{ flex: 1 }} />
              <Typography variant="body2" sx={{ mx: 2, color: 'text.secondary' }}>
                {t('clientAll.or')}
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Box>
            
            {/* Загрузчик файлов */}
            <FileUploader
              value={fileValue}
              isDisabled={isReadOnly}
              onChange={(file) => {
                setFileValue(file);
                // Очищаем URL при загрузке файла
                setUrlValue('');
              }}
              setError={() => {}} // Убираем обработку ошибок
              maxFileSize={6 * 1024 * 1024} // 6MB
              allowedFileTypes={['*/*']} // любые файлы
              buttonText={t('clientAll.selectFile')}
              dragDropText={t('clientAll.dragDropOrPaste')}
            />
          </Grid>
        </Grid>
        
        {/* Убираем отображение ошибок, так как поля опциональные */}
      </Box>
    </Box>
  );
};

export default FileDownloadSection;
